import { CellFormatOptions, ColumnTable, Icon, Table } from '@octano/global-ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TableEmptyContent from '../../../../../../components/text/TableEmptyContent';
import { OtherStudiesPagination, OtherStudy } from '../../types';
import { EducationalInformationLoaderQuery } from '../../types';
import { formatDate } from '../../../../../../utils/dates';
import { ITEMS_PER_PAGE_DEFAULT } from './OtherStudiesLoader';
import { Col } from 'reactstrap';
import { OtherStudiesDeleteModal } from './OtherStudiesDeleteModal';
import { SectionTitle } from '../../../../../../components/text';

interface OtherStudiesProps {
  otherStudies: OtherStudy[];
  paginationData: OtherStudiesPagination;
  query: EducationalInformationLoaderQuery;
  updateQuery: (query: EducationalInformationLoaderQuery) => void;
  toggleEdit: (record: any) => void;
  refresh: () => Promise<void>;
  hasPermission: boolean;
}
export const OtherStudies = ({
  otherStudies,
  paginationData,
  query,
  updateQuery,
  toggleEdit,
  refresh,
  hasPermission,
}: OtherStudiesProps) => {
  const { t } = useTranslation();
  const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number>();

  const texts = useMemo(() => {
    const prefix = 'studentManagement.educationalInformation';
    return {
      title: t(`${prefix}.secondaryTitle`),
      secondaryTitle: t(`${prefix}.secondaryTitle`),
      noRecords: t(`${prefix}.emptyContent.title`),
      noRecordsSubtitle: t(`${prefix}.emptyContent.subtitle`),
      columns: {
        academicLevel: t(`${prefix}.columns.academicLevel`),
        program: t(`${prefix}.columns.program`),
        description: t(`${prefix}.columns.description`),
        country: t(`${prefix}.columns.country`),
        institution: t(`${prefix}.columns.institution`),
        validation: t(`${prefix}.columns.validation`),
        dateOfAcquisition: t(`${prefix}.columns.dateOfAcquisition`),
        action: t(`${prefix}.columns.action`),
      },
      buttonText: t(`${prefix}.buttonText`),
      deleteSuccess: t(`${prefix}.form.deleteSuccess`),
      deleteError: t(`${prefix}.form.deleteError`),
    };
  }, [t]);

  const columns = useMemo<ColumnTable<OtherStudy>[]>(() => {
    const array: ColumnTable[] = [
      {
        headerText: texts.columns.academicLevel,
        columnName: 'academicLevel',
        tdClassName: 'text-center',
        thClassName: 'text-center',
      },
      {
        headerText: texts.columns.program,
        columnName: 'programName',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.description,
        columnName: 'description',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.country,
        columnName: 'country',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.institution,
        columnName: 'institution',
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        headerText: texts.columns.validation,
        columnName: 'validated',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat({ value }) {
          return value ? 'Si' : 'No';
        },
      },
      {
        headerText: texts.columns.dateOfAcquisition,
        columnName: 'graduationDate',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat({ value }) {
          return formatDate(value, 'DD/MM/YYYY');
        },
      },
    ];

    if (hasPermission) {
      array.push({
        headerText: texts.columns.action,
        columnName: 'action',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat(options: CellFormatOptions<OtherStudy>) {
          return (
            <>
              <Col className="d-flex justify-content-center">
                <div
                  onClick={() => {
                    toggleEdit(options.row);
                  }}
                  className="cursor-pointer mr-2"
                >
                  <Icon size="15" name="edit" />
                </div>
                <div
                  onClick={() => {
                    setToggleDeleteModal(true);
                    setIdToDelete(options.row.id);
                  }}
                  className="cursor-pointer"
                >
                  <Icon name="trash" />
                </div>
              </Col>
            </>
          );
        },
      });
    }

    return array;
  }, [texts, toggleEdit, hasPermission]);

  return (
    <>
      <div className="w-100 mt-4">
        {!hasPermission && (
          <Col xs={12} md={6} className="mb-4 pl-0">
            <SectionTitle text={texts.secondaryTitle} />
          </Col>
        )}
        <Table
          columns={columns}
          data={otherStudies}
          pagination={{
            totalItems: paginationData.totalItems,
            onChangePage: (page) => updateQuery({ ...query, page }),
            itemsPerPage: ITEMS_PER_PAGE_DEFAULT,
            totalPages: paginationData.totalPages,
            currentPage: query.page,
          }}
          noResultsText={
            <TableEmptyContent
              title={texts.noRecords}
              subtitle={texts.noRecordsSubtitle}
            />
          }
        />
      </div>
      <OtherStudiesDeleteModal
        toggleDeleteModal={toggleDeleteModal}
        setToggleDeleteModal={setToggleDeleteModal}
        refresh={refresh}
        idToDelete={idToDelete}
      />
    </>
  );
};
